/**
 * Pure, locale-aware value formatters - the single home for date, currency,
 * and number formatting across the admin and consumer apps.
 *
 * Every function takes an explicit `locale` / `currencyCode` - no globals, no
 * implicit state. In components, prefer the `useFormatters()` hook (see
 * `./use-formatters`), which binds these to the organisation's configured
 * locale/currency from `FormattingProvider`. Call these raw functions directly
 * only from non-React code (module-level helpers, tests) where a locale is
 * already in hand.
 *
 * This module is intentionally self-contained (it imports nothing but
 * `./locale`) so both apps and the shared hook can depend on it without
 * creating an import cycle. `@/admin/lib/formatting` re-exports these for
 * backwards compatibility.
 */

// ── Currency ─────────────────────────────────────────────

export function isMoneyObject(
  value: unknown,
): value is { amount?: unknown; currency?: string } {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const record = value as Record<string, unknown>
  const keys = Object.keys(record)
  return (
    keys.length > 0 &&
    typeof record.currency === 'string' &&
    keys.every((k) => k === 'amount' || k === 'currency')
  )
}

export function formatCurrencyValue(
  value: unknown,
  currencyCode: string,
  locale: string,
): string {
  if (value === null || value === undefined || value === '') return '-'

  const resolvedCurrency = currencyCode
  let amount: unknown = value

  // The caller-supplied `currencyCode` (the tenant/organisation's configured
  // currency, fed from FormattingProvider) is authoritative. Any currency code
  // baked into a stored money value is deliberately ignored so all money follows
  // the tenant's currency choice - we only unwrap the amount from the envelope.
  if (isMoneyObject(value)) {
    if (value.amount === null || value.amount === undefined || value.amount === '') return '-'
    amount = value.amount
  } else if (typeof value === 'string' && value.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(value) as Record<string, unknown>
      if (isMoneyObject(parsed)) {
        if (parsed.amount === null || parsed.amount === undefined || parsed.amount === '') return '-'
        amount = parsed.amount
      }
    } catch { /* not JSON, treat as raw value */ }
  }

  const num = typeof amount === 'number' ? amount : Number(amount)
  if (isNaN(num)) return String(value)
  return formatCurrency(num, resolvedCurrency, locale)
}

export function getCurrencySymbol(currencyCode: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
      .formatToParts(0)
      .find((p) => p.type === 'currency')?.value ?? currencyCode
  } catch {
    return currencyCode
  }
}

export function formatCurrency(
  amount: number,
  currencyCode: string,
  locale: string,
): string {
  const hasDecimals = amount % 1 !== 0
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: hasDecimals ? 2 : 0,
    }).format(amount)
  } catch {
    return `${currencyCode} ${amount}`
  }
}

/**
 * Format a compact currency value for dashboards (e.g. £1.23M, £45K).
 */
export function formatCurrencyCompact(
  amount: number,
  currencyCode: string,
  locale: string,
): string {
  try {
    if (Math.abs(amount) >= 1_000_000) {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 2,
        notation: 'compact',
      }).format(amount)
    }
    if (Math.abs(amount) >= 1_000) {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 0,
        notation: 'compact',
      }).format(amount)
    }
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 0,
    }).format(amount)
  } catch {
    return `${currencyCode} ${amount}`
  }
}

// ── Dates ────────────────────────────────────────────────

/** Dates with year >= 9000 are "no end date" sentinels (e.g. 9999-12-31). */
function isSentinelDate(iso: string): boolean {
  const year = parseInt(iso.slice(0, 4), 10)
  return year >= 9000
}

/**
 * Pick the instant to format for a calendar date. Plain `YYYY-MM-DD` and a
 * date the backend sent as UTC midnight (`2026-09-01T00:00:00+00:00`) both mean
 * a day, not a moment: formatting UTC midnight in a US browser shows the day
 * before. Both are pinned to local noon so every time zone shows the same
 * day. Real timestamps pass through unchanged.
 */
const UTC_MIDNIGHT = /^(\d{4}-\d{2}-\d{2})T00:00(?::00(?:\.0+)?)?(?:Z|[+-]00:?00)$/

export function calendarDateInput(iso: string): string {
  if (!iso.includes('T')) return iso + 'T12:00:00'
  const utcMidnight = UTC_MIDNIGHT.exec(iso)
  return utcMidnight ? `${utcMidnight[1]}T12:00:00` : iso
}

/**
 * Format a date string (ISO or YYYY-MM-DD) to a short display format.
 * e.g. formatDate('2025-01-15', 'en-GB') → '15 Jan 2025'
 * Sentinel dates (year >= 9000) return 'No end date'.
 */
export function formatDate(iso: string, locale: string): string {
  if (!iso) return ''
  if (isSentinelDate(iso)) return 'No end date'
  try {
    const dateStr = calendarDateInput(iso)
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(dateStr))
  } catch {
    return iso
  }
}

/**
 * Format a date string (ISO or YYYY-MM-DD) as an all-numeric date.
 * e.g. formatDateNumeric('2025-01-15', 'en-US') → '01/15/2025'
 *      formatDateNumeric('2025-01-15', 'en-GB') → '15/01/2025'
 *
 * Locale-aware on purpose: a US tenant asking for MM/DD/YYYY and a UK
 * tenant expecting DD/MM/YYYY are the same request in two places, so the
 * order comes from the locale rather than a hardcoded pattern. Use where
 * a date is scanned in a column against other dates - the short form
 * ('Jan 15, 2025') reads better in prose but is harder to compare down a
 * column of rows.
 */
export function formatDateNumeric(iso: string, locale: string): string {
  if (!iso) return ''
  if (isSentinelDate(iso)) return 'No end date'
  try {
    const dateStr = calendarDateInput(iso)
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateStr))
  } catch {
    return iso
  }
}

/**
 * Format a date string to a long display format.
 * e.g. formatDateLong('2025-01-15', 'en-GB') → '15 January 2025'
 */
export function formatDateLong(iso: string, locale: string): string {
  if (!iso) return ''
  if (isSentinelDate(iso)) return 'No end date'
  try {
    const dateStr = calendarDateInput(iso)
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(dateStr))
  } catch {
    return iso
  }
}

/**
 * Format a datetime string.
 * e.g. formatDateTime('2025-01-15T14:30:00Z', 'en-GB') → '15 Jan 2025, 14:30'
 */
export function formatDateTime(iso: string, locale: string): string {
  if (!iso) return ''
  try {
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

/**
 * Format a time string.
 * e.g. formatTime('2025-01-15T14:30:00Z', 'en-GB') → '14:30'
 */
export function formatTime(iso: string, locale: string): string {
  if (!iso) return ''
  try {
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

// ── Numbers ──────────────────────────────────────────────

/**
 * Format a plain number with locale-appropriate grouping.
 * e.g. formatNumber(12345, 'en-GB') → '12,345'
 */
export function formatNumber(n: number, locale: string): string {
  try {
    return new Intl.NumberFormat(locale).format(n)
  } catch {
    return String(n)
  }
}
